import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import {
    catchError,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Update } from '@ngrx/entity';

import { NotificationsEntity } from '../models/notifications';
import * as NotificationsActions from './notifications.actions';
import * as NotificationsFeature from './notifications.reducer';
import * as NotificationsSelectors from './notifications.selectors';
import { PortalsNotificationsService } from '../services/portals-notifications.service';
import { PortalsSocketNotificationsService } from '../services/portals-socket-notifications.service';

@Injectable()
export class NotificationsEffects {
    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.init),
            fetch({
                run: () => {
                    // Your custom service 'load' logic goes here. For now just dispatch a success action...
                    return NotificationsActions.loadNotifications();
                },
                onError: (action, error) => {
                    console.error(error);
                    return NotificationsActions.loadNotificationsFailure({
                        error,
                    });
                },
            }),
        ),
    );

    loadNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.loadNotifications),
            switchMap(() =>
                this.notificationsService.getNotifications().pipe(
                    map(notifications =>
                        NotificationsActions.loadNotificationsSuccess({
                            notifications,
                        }),
                    ),
                    catchError(error =>
                        of(
                            NotificationsActions.loadNotificationsFailure({
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    loadNotificationsFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(NotificationsActions.loadNotificationsFailure),
                tap(action => {
                    this.router.navigate(
                        [`/my-portal/error/${action.error.status}`],
                        {
                            queryParams: {
                                source: this.router.url,
                                errorName: action.error.statusText,
                            },
                        }
                    );
                })
            ),
        { dispatch: false }
    );

    loadMoreNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.loadMoreNotifications),
            withLatestFrom(
                this.store.pipe(select(NotificationsSelectors.getLoadedPage)),
            ),
            mergeMap(([, loadedPage]) =>
                this.notificationsService
                    .getNotifications((loadedPage ?? 0) + 1)
                    .pipe(
                        map(notifications =>
                            NotificationsActions.loadNotificationsSuccess({
                                notifications,
                            }),
                        ),
                        catchError(error =>
                            of(
                                NotificationsActions.loadNotificationsFailure({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    markAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.markNotificationAsRead),
            mergeMap(({ notification }) =>
                this.notificationsService.markAsRead(notification.id).pipe(
                    map(() =>
                        NotificationsActions.markNotificationAsReadSuccess({
                            notification: {
                                id: notification.id,
                                changes: { read: true },
                            },
                        }),
                    ),
                    catchError(error =>
                        of(
                            NotificationsActions.markNotificationAsReadFailure(
                                {
                                    error,
                                },
                            ),
                        ),
                    ),
                ),
            ),
        ),
    );

    markAllAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.markAllNotificationsAsRead),
            withLatestFrom(
                this.store.pipe(
                    select(NotificationsSelectors.getAllNotifications),
                ),
            ),
            mergeMap(([, notifications]) =>
                this.notificationsService.markAllAsRead().pipe(
                    map(() => {
                        const updates = notifications
                            .filter(n => !n.read)
                            .map(n => ({
                                id: n.id,
                                changes: {
                                    read: true,
                                },
                            }));
                        return NotificationsActions.markAllNotificationsAsReadSuccess(
                            {
                                notifications: updates,
                            },
                        );
                    }),
                    catchError(error =>
                        of(
                            NotificationsActions.markAllNotificationsAsReadFailure(
                                {
                                    error,
                                },
                            ),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.deleteNotification),
            mergeMap(({ notification }) =>
                this.notificationsService.deleteNotification(notification).pipe(
                    map(unread =>
                        NotificationsActions.deleteNotificationSuccess({
                            unread,
                        })
                    ),
                    catchError(error =>
                        of(
                            NotificationsActions.deleteNotificationFailure({
                                error,
                                restore: notification,
                            })
                        )
                    )
                )
            )
        )
    );

    deleteNotificationFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(NotificationsActions.deleteNotificationFailure),
                tap(() => {
                    this.toastr.error(
                        'Something went wrong deleting your notification.'
                    );
                })
            ),
        { dispatch: false }
    );

    toggleNotificationRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.toggleNotificationRead),
            mergeMap(({ notification }) =>
                this.notificationsService.toggleNotificationRead(notification).pipe(
                    map(unread =>
                        NotificationsActions.toggleNotificationReadSuccess({
                            unread,
                        })
                    ),
                    catchError(error =>
                        of(
                            NotificationsActions.toggleNotificationReadFailure({
                                error,
                                restore: notification,
                            })
                        )
                    )
                )
            )
        )
    );

    toggleNotificationReadFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(NotificationsActions.toggleNotificationReadFailure),
                tap(() => {
                    this.toastr.error(
                        'Something went wrong updating your notification.'
                    );
                })
            ),
        { dispatch: false }
    );

    selectNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.selectNotification),
            mergeMap(({ notification }) => {
                if (notification.read) {
                    return EMPTY;
                }

                const update: Update<NotificationsEntity> = {
                    id: notification.id,
                    changes: { read: true },
                };

                return of(
                    NotificationsActions.toggleNotificationRead({
                        notification: update,
                    })
                );
            })
        )
    );

    loadUnreadNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.loadUnreadNotifications),
            switchMap(() =>
                this.notificationsService.getUnreadNotificationsCount().pipe(
                    map(unread =>
                        NotificationsActions.loadUnreadNotificationsSuccess({
                            unread,
                        })
                    ),
                    catchError(error =>
                        of(
                            NotificationsActions.loadUnreadNotificationsFailure(
                                { error }
                            )
                        )
                    )
                )
            )
        )
    );

    initializeEchoListener$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.initializeEchoListener),
            switchMap(({ token, userId }) =>
                this.echoService.registerEchoListener(token, userId).pipe(
                    map(notification =>
                        NotificationsActions.echoNotificationReceived({
                            notification,
                        })
                    ),
                    takeUntil(
                        this.actions$.pipe(
                            ofType(
                                NotificationsActions.deinitializeEchoListener
                            )
                        )
                    )
                )
            )
        )
    );

    deinitializeEchoListener$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(NotificationsActions.deinitializeEchoListener),
                tap(() => this.echoService.deregisterEchoListener())
            ),
        { dispatch: false }
    );

    echoNotificationReceived$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(NotificationsActions.echoNotificationReceived),
                tap(({ notification }) => {
                    this.notificationsService.browserNotification$.next(
                        notification
                    );
                    this.notificationsService.showNotificationToast(
                        notification
                    );
                })
            ),
        { dispatch: false }
    );

    constructor(
        private readonly actions$: Actions,
        private readonly notificationsService: PortalsNotificationsService,
        private readonly echoService: PortalsSocketNotificationsService,
        private readonly toastr: ToastrService,
        private readonly router: Router,
        private readonly store: Store<NotificationsFeature.State>
    ) {}
}
